import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cloneElement, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------
const TOOLTIP_SHOW_DELAY_MS = 300;

/**
 * Hover/focus tooltip, portaled to <body> so it's never clipped by a card's
 * rounded corners or a table's `overflow-x-auto` scroller. Wraps a single
 * child element (a button, badge, label, icon…) and adds the hover/focus
 * handlers plus `aria-describedby` — the child itself needs no changes.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: ReactNode;
  children: ReactElement<any>;
  side?: 'top' | 'bottom';
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const id = useId();

  const show = () => {
    window.clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(true), TOOLTIP_SHOW_DELAY_MS);
  };
  const hide = () => {
    window.clearTimeout(timerRef.current);
    setOpen(false);
  };

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setCoords(
      side === 'bottom'
        ? { top: rect.bottom + 6, left: rect.left + rect.width / 2 }
        : { top: rect.top - 6, left: rect.left + rect.width / 2 },
    );
  }, [open, side]);

  if (!content) return children;

  const child = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
      const { ref } = children as any;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    onMouseEnter: (e: any) => {
      children.props.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: any) => {
      children.props.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: any) => {
      children.props.onFocus?.(e);
      show();
    },
    onBlur: (e: any) => {
      children.props.onBlur?.(e);
      hide();
    },
    'aria-describedby': open ? id : undefined,
  });

  return (
    <>
      {child}
      {open &&
        coords &&
        createPortal(
          <div
            role="tooltip"
            id={id}
            className={cx(
              'pointer-events-none fixed z-[100] max-w-xs -translate-x-1/2 rounded-md bg-ink-900 px-2.5 py-1.5 text-xs',
              'font-normal leading-snug text-white shadow-lg',
              side === 'bottom' ? 'translate-y-0' : '-translate-y-full',
            )}
            style={{ top: coords.top, left: coords.left }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * A small "i" glyph that carries a tooltip — for explaining a label or
 * concept that has no natural hoverable text of its own (a field caption,
 * a table header, a stat name).
 */
export function InfoTooltip({ content, side }: { content: ReactNode; side?: 'top' | 'bottom' }) {
  return (
    <Tooltip content={content} side={side}>
      <button
        type="button"
        tabIndex={0}
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-ink-400 hover:text-ink-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
      >
        <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5" aria-hidden>
          <path
            fillRule="evenodd"
            d="M8 15A7 7 0 108 1a7 7 0 000 14zm.75-10.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM7 7a.75.75 0 000 1.5h.25v3a.75.75 0 001.5 0v-3.5A.75.75 0 008 7H7z"
            clipRule="evenodd"
          />
        </svg>
        <span className="sr-only">More info</span>
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-accent-600 text-white hover:bg-accent-700 disabled:bg-accent-300',
  secondary: 'bg-white text-ink-800 ring-1 ring-inset ring-ink-200 hover:bg-ink-50',
  ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
  danger: 'bg-white text-red-700 ring-1 ring-inset ring-red-200 hover:bg-red-50',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500',
        size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm',
        BUTTON_STYLES[variant],
        className,
      )}
    >
      {loading && <Spinner className="h-3.5 w-3.5" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className ?? 'h-4 w-4')} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------
export function Field({
  label,
  hint,
  tip,
  children,
}: {
  label: string;
  hint?: string;
  /** Tooltip content explaining the field, shown via an "i" glyph next to the label. */
  tip?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1 text-xs font-medium text-ink-700">
        {label}
        {tip && <InfoTooltip content={tip} />}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

const CONTROL_CLASSES =
  'w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-ink-900 shadow-sm ring-1 ring-inset ring-ink-200 ' +
  'placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-accent-500 disabled:bg-ink-50';

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(CONTROL_CLASSES, className)} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(CONTROL_CLASSES, 'leading-relaxed', className)} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={cx(CONTROL_CLASSES, 'pr-8', className)}>
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------
export function Card({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cx('rounded-lg bg-white shadow-sm ring-1 ring-ink-200/70', className)}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
          {action}
        </header>
      )}
      <div className={cx('p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  sub,
  tone = 'default',
  tip,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'positive' | 'warning';
  /** Tooltip content explaining what the stat measures, shown via an "i" glyph next to the label. */
  tip?: ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-ink-200/70">
      <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-ink-500">
        {label}
        {tip && <InfoTooltip content={tip} />}
      </p>
      <p
        className={cx(
          'mt-1 text-2xl font-semibold tabular-nums',
          tone === 'positive' && 'text-emerald-600',
          tone === 'warning' && 'text-amber-600',
          tone === 'default' && 'text-ink-900',
        )}
      >
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-ink-500">{sub}</p>}
    </div>
  );
}

const BADGE_TONES: Record<string, string> = {
  neutral: 'bg-ink-100 text-ink-700',
  blue: 'bg-accent-50 text-accent-700 ring-1 ring-inset ring-accent-200',
  green: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
  amber: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  purple: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200',
};

export type BadgeTone = keyof typeof BADGE_TONES;

export function Badge({
  tone = 'neutral',
  title,
  children,
}: {
  tone?: BadgeTone;
  /** Tooltip content — for a badge whose meaning needs more room than the label has. */
  title?: ReactNode;
  children: ReactNode;
}) {
  const span = (
    <span
      tabIndex={title ? 0 : undefined}
      className={cx(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap',
        Boolean(title) && 'cursor-help',
        BADGE_TONES[tone],
      )}
    >
      {children}
    </span>
  );
  return title ? <Tooltip content={title}>{span}</Tooltip> : span;
}

export const STATUS_TONES: Record<string, BadgeTone> = {
  new: 'neutral',
  contacted: 'blue',
  replied: 'purple',
  won: 'green',
  lost: 'red',
  archived: 'neutral',
  drafted: 'amber',
  approved: 'blue',
  scheduled: 'blue',
  sent: 'green',
  failed: 'red',
  skipped: 'neutral',
  interested: 'green',
  warm: 'green',
  objection: 'amber',
  waiting: 'blue',
  cold: 'red',
  unclear: 'neutral',
  active: 'green',
  paused: 'amber',
  closed: 'neutral',
  idea: 'neutral',
  published: 'green',
  // Deal stages. `won`/`lost` are already above and shared with prospect status.
  lead: 'neutral',
  qualified: 'blue',
  quoted: 'purple',
  negotiating: 'amber',
};

/** What each status actually means — surfaced as a tooltip since the label alone is often ambiguous. */
export const STATUS_DESCRIPTIONS: Record<string, string> = {
  new: 'Not yet contacted.',
  contacted: 'At least one message has been sent.',
  replied: 'The prospect has replied.',
  won: 'Converted — deal closed.',
  lost: 'No longer being pursued.',
  archived: 'Removed from active outreach.',
  drafted: 'A message has been drafted but not yet approved.',
  approved: 'Approved by an admin — eligible to send or schedule.',
  scheduled: 'Approved and queued to send at a future time.',
  sent: 'Delivered via Gmail.',
  failed: 'The send attempt errored.',
  skipped: 'Deliberately not sent (e.g. suppressed or superseded).',
  interested: 'Reply signals genuine interest.',
  warm: 'Reply is positive but non-committal.',
  objection: 'Reply raises a concern or pushback.',
  waiting: 'Reply asks to follow up later.',
  cold: 'Reply signals no interest.',
  unclear: "Reply couldn't be confidently classified.",
  active: 'Currently running.',
  paused: 'Temporarily halted — can be resumed.',
  closed: 'Finished and no longer running.',
  idea: 'Not yet published.',
  published: 'Live and publicly visible.',
};

export function StatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return <Badge>—</Badge>;
  return (
    <Badge tone={STATUS_TONES[status] ?? 'neutral'} title={STATUS_DESCRIPTIONS[status]}>
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}

/**
 * Flags an address that is only a guess, so nobody reads it as confirmed.
 * Renders nothing for a verified address or for no address at all — the
 * badge marks the one state that looks real and isn't.
 */
export function EmailStatusBadge({ status }: { status: string | null | undefined }) {
  if (status !== 'inferred') return null;
  return (
    <Badge
      tone="amber"
      title="A guessed address (e.g. from the organisation's email pattern). Nothing is sent to it until it is confirmed."
    >
      unconfirmed
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------
export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-ink-500">
      <Spinner />
      {label}…
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md bg-red-50 px-3 py-2.5 text-sm text-red-800 ring-1 ring-inset ring-red-200">
      <span>{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="shrink-0 font-medium underline underline-offset-2">
          Retry
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-ink-200 px-6 py-10 text-center">
      <p className="text-sm font-medium text-ink-800">{title}</p>
      {description && <p className="mx-auto mt-1 max-w-md text-sm text-ink-500">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export function Modal({
  open,
  onClose,
  title,
  children,
  width = 'max-w-lg',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/40 p-4 sm:p-8">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cx('relative w-full rounded-lg bg-white shadow-xl', width)}
      >
        <header className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </header>
        <div className="max-h-[75vh] overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
/** Offset/limit pager for a server-paginated list. `total` is the full count the server reports. */
export function Pagination({
  offset,
  limit,
  total,
  onOffsetChange,
}: {
  offset: number;
  limit: number;
  total: number;
  onOffsetChange: (offset: number) => void;
}) {
  if (total <= limit && offset === 0) return null;

  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + limit, total);

  return (
    <div className="flex items-center justify-between border-t border-ink-100 px-4 py-3 text-sm text-ink-600">
      <span>
        {rangeStart}–{rangeEnd} of {total}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => onOffsetChange(Math.max(0, offset - limit))} disabled={page <= 1}>
          Previous
        </Button>
        <span className="tabular-nums text-ink-500">
          Page {page} of {pageCount}
        </span>
        <Button
          size="sm"
          onClick={() => onOffsetChange(offset + limit)}
          disabled={page >= pageCount}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-ink-100 text-sm">{children}</table>
    </div>
  );
}

export type SortDir = 'asc' | 'desc';

export function Th({
  children,
  className,
  tip,
  sort,
}: {
  children?: ReactNode;
  className?: string;
  /** Tooltip content explaining the column, shown via an "i" glyph next to the header. */
  tip?: ReactNode;
  /**
   * Makes the header a sort toggle. `dir` is the column's current direction,
   * or null when the table is sorted by something else.
   */
  sort?: { dir: SortDir | null; onSort: () => void };
}) {
  return (
    <th
      scope="col"
      aria-sort={sort?.dir ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cx(
        'whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500',
        className,
      )}
    >
      <span className="inline-flex items-center gap-1">
        {sort ? (
          <button
            type="button"
            onClick={sort.onSort}
            className={cx(
              'group inline-flex items-center gap-1 uppercase tracking-wide hover:text-ink-900',
              sort.dir && 'text-ink-900',
            )}
          >
            {children}
            <span
              aria-hidden="true"
              className={cx('text-[10px]', sort.dir ? 'text-accent-600' : 'text-ink-300 group-hover:text-ink-500')}
            >
              {sort.dir === 'desc' ? '▼' : '▲'}
            </span>
          </button>
        ) : (
          children
        )}
        {tip && <InfoTooltip content={tip} />}
      </span>
    </th>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cx('px-3 py-2.5 align-middle text-ink-800', className)}>{children}</td>;
}
